import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Users, Mail, Phone, Building2, Pencil, Save, X, Calendar, Shield, BadgeCheck, Lock, Key, Camera, Trash2, ChevronRight, Sparkles, Clock, MapPin, Plus, CheckCircle2, ClipboardList } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { getMyProfile, updateMyProfile, uploadMyFoto, removeMyFoto, removeRepresentativeToken, getRepEvents, getRepAssociates } from '../services/representativeService';
import { getSupportTodo, SupportTodo } from '../services/representativeSupportService';
import AssociativeAppShell from '../components/AssociativeAppShell';
import { getProvinceName } from '../utils/provinceNames';
import { greetingFor, romeHour, useClock, useWeather, WeatherScene, WeatherInfo } from '../components/admin/v2/DashboardWelcome';
import { useCityCoordinates } from '../hooks/useCityCoordinates';

const SUPPORT_STATUS_LABEL: Record<string, string> = {
  aperto: 'Aperto',
  preso_in_carico: 'Preso in carico',
  in_lavorazione: 'In lavorazione',
  in_attesa_associato: 'In attesa associato',
};
const SUPPORT_PRIORITY_LABEL: Record<string, string> = {
  bassa: 'Bassa', media: 'Media', alta: 'Alta', urgente: 'Urgente',
};

interface CaricaEntry {
  caricaId: { _id: string; nome: string; categoria: string; settore: string; livello: number } | null;
  dataInizio: string;
  dataFine: string | null;
}

interface RepProfile {
  _id: string;
  nome: string;
  cognome: string;
  dataDiNascita?: string;
  luogoDiNascita?: string;
  cellulare?: string;
  emailPersonale?: string;
  foto?: string;
  tipo: 'interno' | 'esterno';
  status: string;
  residenza?: { indirizzo?: string; civico?: string; citta?: string; provincia?: string; cap?: string };
  associateId?: { nominativo: string; partitaIVA: string; provincia: string; regione: string; citta: string; categoria: string; email: string };
  cariche: CaricaEntry[];
  permissions: { name: string; description: string }[];
  associatesAccess?: boolean;
  pin?: boolean;
}

interface RepEvent {
  _id: string;
  nome: string;
  tipologiaId: { _id: string; nome: string; colore: string } | null;
  dataEvento: string;
  oraInizio: string;
  oraFine: string;
  luogo: string;
  stato: string;
  invitationCount: number;
  isOwner?: boolean;
  verbaleDaLeggere?: boolean;
}

const SETTORE_COLORS: Record<string, string> = {
  autoscuole: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  studi: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  entrambi: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

// Stato evento → badge del design system (come in AssociativeCalendar)
const STATO_BADGE_CLASS: Record<string, string> = {
  BOZZA: 's-bozza',
  PUBBLICATO: 's-programmato',
  IN_CORSO: 's-inviato',
  CONCLUSO: 's-concluso',
};

const STATO_LABEL: Record<string, string> = {
  BOZZA: 'Bozza',
  PUBBLICATO: 'Pubblicato',
  IN_CORSO: 'In corso',
  CONCLUSO: 'Concluso',
  ANNULLATO: 'Annullato',
};

const statoBadge = (stato: string): { className: string; style?: React.CSSProperties } => {
  if (stato === 'ANNULLATO') return { className: '', style: { background: '#FBEDEB', color: '#B23A2C' } };
  return { className: STATO_BADGE_CLASS[stato] || 's-bozza' };
};

const MESI_SHORT = ['GEN', 'FEB', 'MAR', 'APR', 'MAG', 'GIU', 'LUG', 'AGO', 'SET', 'OTT', 'NOV', 'DIC'];

const dateChipParts = (dateStr: string): { m: string; g: string } => {
  try {
    const d = new Date(dateStr);
    return { m: MESI_SHORT[d.getMonth()] || '', g: String(d.getDate()).padStart(2, '0') };
  } catch {
    return { m: '', g: '--' };
  }
};

const AssociativeAreaDashboard: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [profile, setProfile] = useState<RepProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Eventi per agenda dashboard + KPI
  const [events, setEvents] = useState<RepEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [totAssociati, setTotAssociati] = useState<number | null>(null);
  const [scope, setScope] = useState<{ level?: string; regione?: string | null; provincia?: string | null; regioni?: string[]; province?: string[] } | null>(null);
  const [todo, setTodo] = useState<SupportTodo | null>(null);
  const [loadingTodo, setLoadingTodo] = useState(false);

  // Edit form
  const [editCellulare, setEditCellulare] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editResidenza, setEditResidenza] = useState({ indirizzo: '', civico: '', citta: '', provincia: '', cap: '' });
  const [editPin, setEditPin] = useState('');
  // Saluto, orologio e meteo sulla città di residenza del rappresentante (fallback Roma)
  const clock = useClock();
  const weatherLocation = useCityCoordinates(profile?.residenza?.citta, profile?.residenza?.provincia);
  const { weather, unavailable: weatherUnavailable } = useWeather(weatherLocation);
  const [pinAction, setPinAction] = useState<'none' | 'set' | 'remove'>('none');

  useEffect(() => {
    const token = localStorage.getItem('representative_token');
    if (!token) {
      // Se ha il token associato, manda alla dashboard dove la card gestisce il flusso PIN
      const associateToken = localStorage.getItem('associate_token');
      navigate(associateToken ? '/area-riservata' : '/accesso-associati');
      return;
    }

    const load = async () => {
      try {
        const res = await getMyProfile();
        if (res.success) {
          setProfile(res.data);
        }
      } catch (err: any) {
        if (err.response?.status === 401 || err.response?.status === 403 || err.response?.status === 404) {
          // 404 incluso: token rappresentante orfano (es. dopo restore di backup) → pulisci e rifà login
          removeRepresentativeToken();
          const associateToken = localStorage.getItem('associate_token');
          navigate(associateToken ? '/area-riservata' : '/accesso-associati');
          return;
        }
        toast.error('Errore caricamento profilo');
      } finally {
        setLoading(false);
      }
    };
    load();
    // Esegui una sola volta al mount: evita loop di richieste a /me se l'endpoint
    // restituisce ripetutamente errore (navigate/toast sono stabili / catturati una volta).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Eventi (agenda + KPI) e conteggio associati: best-effort, silenziosi
  useEffect(() => {
    if (!profile) return;
    const hasCal = profile.permissions?.some((p) => p.name === 'associativa.eventi.read');
    if (hasCal) {
      getRepEvents({ page: '1', limit: '100' })
        .then((res) => { if (res.success) setEvents(res.data || []); })
        .catch((err) => console.error('Errore caricamento eventi dashboard:', err))
        .finally(() => setLoadingEvents(false));
    } else {
      setLoadingEvents(false);
    }
    // Ticket di supporto "da fare" (solo se operatore di almeno un canale): best-effort.
    if (profile.permissions?.some((p) => p.name === 'associativa.supporto.read')) {
      setLoadingTodo(true);
      getSupportTodo().then(setTodo).catch(() => { /* niente sezione ticket */ }).finally(() => setLoadingTodo(false));
    }
    if (profile.associatesAccess) {
      getRepAssociates({ page: '1', limit: '1' })
        .then((res) => {
          setTotAssociati(res.pagination?.total ?? null);
          if (res.scope) setScope(res.scope);
        })
        .catch((err) => console.error('Errore conteggio associati:', err));
    }
  }, [profile]);

  const startEdit = () => {
    if (!profile) return;
    setEditCellulare(profile.cellulare || '');
    setEditEmail(profile.emailPersonale || '');
    setEditResidenza({
      indirizzo: profile.residenza?.indirizzo || '',
      civico: profile.residenza?.civico || '',
      citta: profile.residenza?.citta || '',
      provincia: profile.residenza?.provincia || '',
      cap: profile.residenza?.cap || ''
    });
    setEditPin('');
    setPinAction('none');
    setEditing(true);
  };

  const handleSave = async () => {
    if (!profile) return;
    setSaving(true);
    try {
      const payload: any = {
        cellulare: editCellulare,
        emailPersonale: editEmail.trim() || undefined,
        residenza: editResidenza,
      };
      if (pinAction === 'set' && editPin) {
        payload.pin = editPin;
      } else if (pinAction === 'remove') {
        payload.pin = null;
      }

      const res = await updateMyProfile(payload);
      if (res.success) {
        setProfile({ ...profile, cellulare: editCellulare, emailPersonale: editEmail.trim() || profile.emailPersonale, residenza: editResidenza, pin: pinAction === 'set' ? true : pinAction === 'remove' ? false : profile.pin });
        setEditing(false);
        toast.success('Profilo aggiornato');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Errore aggiornamento');
    } finally {
      setSaving(false);
    }
  };

  const activeCariche = (profile?.cariche || [])
    .filter(c => !c.dataFine)
    .sort((a, b) => (b.caricaId?.livello || 0) - (a.caricaId?.livello || 0));

  const primaryCarica = activeCariche[0]?.caricaId?.nome;
  const hasLivePerm = profile?.permissions?.some((p) => p.name === 'associativa.live.publish');
  const hasCircolariPerm = profile?.permissions?.some((p) => p.name === 'associativa.circolari.read') ?? false;
  const hasSondaggiPerm = profile?.permissions?.some((p) => p.name === 'associativa.sondaggi.read') ?? false;
  const hasCalPerm = profile?.permissions?.some((p) => p.name === 'associativa.eventi.read');
  const hasCreatePerm = profile?.permissions?.some((p) => p.name === 'associativa.eventi.create');

  if (loading) {
    return (
      <AssociativeAppShell route="dashboard">
        <div className="page">
          <div className="muted" style={{ textAlign: 'center', padding: '80px 0', fontSize: '13px' }}>Caricamento…</div>
        </div>
      </AssociativeAppShell>
    );
  }

  if (!profile) {
    return (
      <AssociativeAppShell route="dashboard">
        <div className="page">
          <div className="muted" style={{ textAlign: 'center', padding: '80px 0', fontSize: '13px' }}>Profilo non disponibile</div>
        </div>
      </AssociativeAppShell>
    );
  }

  const initials = `${profile.nome?.[0] || ''}${profile.cognome?.[0] || ''}`.toUpperCase();

  const UPLOADS_BASE = process.env.REACT_APP_API_BASE_URL?.replace('/api', '') || 'http://localhost:5000';

  const handleFotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('La foto non puo superare i 2 MB');
      return;
    }
    try {
      const res = await uploadMyFoto(file);
      if (res.success) {
        setProfile({ ...profile, foto: res.fotoUrl });
        toast.success('Foto aggiornata');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Errore caricamento foto');
    }
  };

  const handleFotoRemove = async () => {
    try {
      await removeMyFoto();
      setProfile({ ...profile, foto: undefined });
      toast.success('Foto rimossa');
    } catch {
      toast.error('Errore rimozione foto');
    }
  };

  // ——— Agenda: prossimi / conclusi / KPI ———
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const now = new Date();

  const prossimi = events
    .filter((e) => e.stato !== 'CONCLUSO' && e.stato !== 'ANNULLATO' && new Date(e.dataEvento) >= startOfToday)
    .sort((a, b) => new Date(a.dataEvento).getTime() - new Date(b.dataEvento).getTime())
    .slice(0, 5);

  const recenti = events
    .filter((e) => e.stato === 'CONCLUSO')
    .sort((a, b) => new Date(b.dataEvento).getTime() - new Date(a.dataEvento).getTime())
    .slice(0, 3);

  const eventiMese = events.filter((e) => {
    const d = new Date(e.dataEvento);
    return e.stato !== 'ANNULLATO' && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;

  const conclusiTotali = events.filter((e) => e.stato === 'CONCLUSO').length;
  const meseLabel = now.toLocaleDateString('it-IT', { month: 'long' });

  // "Da fare": (1) le mie bozze da completare; (2) eventi (visibili o a cui sono
  // invitato) con un verbale inserito che non ho ancora letto.
  const bozze = events.filter((e) => e.stato === 'BOZZA' && e.isOwner);
  const verbaliDaLeggere = events.filter((e) => e.verbaleDaLeggere);
  const ticketTodo = todo?.items || [];
  const daFareCount = bozze.length + verbaliDaLeggere.length + (todo?.counts.totale || 0);

  const greeting = greetingFor(romeHour(clock));
  const clockLabel = clock.toLocaleTimeString('it-IT', { timeZone: 'Europe/Rome', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const clockDate = clock.toLocaleDateString('it-IT', { timeZone: 'Europe/Rome', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  // Perimetro territoriale dallo scope reale (es. "Provincia di Chieti"),
  // con fallback al settore della carica principale
  const perimetroLabel = (() => {
    // Ripiego sui campi singoli se la risposta non porta gli elenchi
    const regioniScope = scope?.regioni ?? (scope?.regione ? [scope.regione] : []);
    const provinceScope = scope?.province ?? (scope?.provincia ? [scope.provincia] : []);
    if (scope?.level === 'provinciale' && provinceScope.length > 0) {
      return `${provinceScope.length > 1 ? 'Province di' : 'Provincia di'} ${provinceScope.map(getProvinceName).join(', ')}`;
    }
    if (scope?.level === 'regionale' && regioniScope.length > 0) return regioniScope.join(', ');
    if (scope?.level === 'nazionale') return 'Perimetro nazionale';
    return activeCariche[0]?.caricaId?.settore || '';
  })();

  // "tra 3 giorni" / "domani" / "oggi" / "il 24 giugno"
  const relativeDay = (dateStr: string): string => {
    const d = new Date(dateStr);
    d.setHours(0, 0, 0, 0);
    const diff = Math.round((d.getTime() - startOfToday.getTime()) / 86400000);
    if (diff <= 0) return 'oggi';
    if (diff === 1) return 'domani';
    if (diff <= 7) return `tra ${diff} giorni`;
    return `il ${new Date(dateStr).toLocaleDateString('it-IT', { day: 'numeric', month: 'long' })}`;
  };

  // Sottotitolo "vivo" come da mockup: data odierna + impegni reali
  const heroSub = (() => {
    const oggiLabel = now.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });
    const frasi: string[] = [];
    if (hasCalPerm && !loadingEvents) {
      if (bozze.length > 0) {
        frasi.push(`hai ${bozze.length} event${bozze.length === 1 ? 'o' : 'i'} in bozza da completare`);
      }
      const prossimoPub = prossimi.find((e) => e.stato !== 'BOZZA');
      if (prossimoPub) {
        frasi.push(`il prossimo appuntamento è «${prossimoPub.nome}» ${relativeDay(prossimoPub.dataEvento)}`);
      }
    }
    let coda: string;
    if (frasi.length > 0) {
      const testo = frasi.join(' e ');
      coda = testo.charAt(0).toUpperCase() + testo.slice(1) + '.';
    } else if (hasCalPerm && !loadingEvents) {
      coda = 'Non hai impegni imminenti: l\'agenda è libera.';
    } else if (activeCariche.length > 0) {
      coda = `${activeCariche.length} caric${activeCariche.length === 1 ? 'a' : 'he'} attiv${activeCariche.length === 1 ? 'a' : 'e'} nel tuo ruolo associativo.`;
    } else {
      coda = 'Nessuna carica attiva al momento.';
    }
    return `Oggi è ${oggiLabel}. ${coda}`;
  })();

  // Riga evento in stile mockup (date-chip + badge + meta)
  const EventRow: React.FC<{ ev: RepEvent }> = ({ ev }) => {
    const chip = dateChipParts(ev.dataEvento);
    const stato = statoBadge(ev.stato);
    const open = () => navigate(`/area-associativa/calendario/${ev._id}`);
    return (
      <div className="event-row" onClick={open} role="button" tabIndex={0}
           onKeyDown={(e) => { if (e.key === 'Enter') open(); }}>
        <div className="date-chip">
          <span className="m">{chip.m}</span>
          <span className="g">{chip.g}</span>
        </div>
        <div className="info">
          <div className="t">
            {ev.nome}
            <span className={`badge ${stato.className}`} style={stato.style}>{STATO_LABEL[ev.stato] || ev.stato}</span>
          </div>
          <div className="meta">
            {ev.tipologiaId && (
              <span className="badge" style={{ padding: '2px 9px', fontSize: '10.5px', background: `${ev.tipologiaId.colore}22`, color: ev.tipologiaId.colore }}>
                {ev.tipologiaId.nome}
              </span>
            )}
            {ev.oraInizio && <span><Clock size={13} /> {ev.oraInizio}{ev.oraFine ? ` - ${ev.oraFine}` : ''}</span>}
            {ev.luogo && <span><MapPin size={13} /> <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '200px' }}>{ev.luogo}</span></span>}
          </div>
        </div>
        <div className="right">
          <span className="inv"><Users size={14} /> {ev.invitationCount.toLocaleString('it-IT')}</span>
          <ChevronRight size={15} className="chev" />
        </div>
      </div>
    );
  };

  // Card Cariche Attive (riusata in colonna destra)
  const caricheCard = activeCariche.length > 0 && (
    <section className="card">
      <div className="card-head">
        <BadgeCheck size={16} />
        <h2>Cariche Attive</h2>
      </div>
      <div className="card-body" style={{ display: 'grid', gap: '10px' }}>
        {activeCariche.map((c, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: 'var(--un-green-50)', border: '1px solid var(--un-green-100)', borderRadius: 'var(--r-md)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '34px', height: '34px', borderRadius: 'var(--r-md)', background: 'var(--un-green-100)', color: 'var(--un-green-700)', display: 'grid', placeItems: 'center' }}>
                <Shield size={16} />
              </div>
              <div>
                <div style={{ font: '600 13px var(--font-body)', color: 'var(--text-strong)' }}>{c.caricaId?.nome || 'Carica'}</div>
                <div className="muted" style={{ fontSize: '11.5px' }}>dal {new Date(c.dataInizio).toLocaleDateString('it-IT')}</div>
              </div>
            </div>
            {c.caricaId?.settore && (
              <span className={`px-2 py-0.5 text-2xs font-medium rounded-full ${SETTORE_COLORS[c.caricaId.settore] || SETTORE_COLORS.entrambi}`}>
                {c.caricaId.settore}
              </span>
            )}
          </div>
        ))}
      </div>
    </section>
  );

  return (
    <AssociativeAppShell
      nome={profile.nome}
      cognome={profile.cognome}
      caricaPrincipale={primaryCarica}
      tipo={profile.tipo}
      route="dashboard"
      associatesAccess={profile.associatesAccess}
      canLive={hasLivePerm}
      canCircolari={hasCircolariPerm}
      canSondaggi={hasSondaggiPerm}
      canCalendario={!!hasCalPerm}
      foto={profile.foto}
      perimetro={activeCariche[0]?.caricaId?.settore}
      caricaDal={activeCariche[0]?.dataInizio}
    >
      <div className="page fade-in">
        {/* Hero benvenuto — verde come da mockup */}
        <section className="hero">
          {weather && <WeatherScene weather={weather} now={clock} className="aa-weather-scene" />}
          <div
            className="hero-row"
            style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '24px', flexWrap: 'wrap', width: '100%' }}
          >
            <div className="grow" style={{ flex: '1 1 320px', minWidth: 0 }}>
              <div className="kicker">
                Area Rappresentativa{perimetroLabel ? ` · ${perimetroLabel}` : ''}
              </div>
              <div className="aa-clock">{clockDate}<time dateTime={clock.toISOString()}>{clockLabel}</time></div>
              <h1>{greeting}, {profile.nome}.</h1>
              <p className="sub">{heroSub}</p>
              <WeatherInfo weather={weather} unavailable={weatherUnavailable} label={weatherLocation.label} className="aa-weather-info" />
            </div>
            <div
              className="hero-actions"
              style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'flex-end', marginLeft: 'auto', paddingRight: '4px', flex: '0 0 auto' }}
            >
              {hasCreatePerm ? (
                <button className="btn primary" type="button" onClick={() => navigate('/area-associativa/calendario/nuovo')}>
                  <Plus size={15} /> Nuovo evento
                </button>
              ) : hasCalPerm && (
                <button className="btn primary" type="button" onClick={() => navigate('/area-associativa/calendario')}>
                  <Calendar size={15} /> Calendario
                </button>
              )}
              {profile.associatesAccess && (
                <button className="btn dark" type="button"
                        style={{ background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.2)' }}
                        onClick={() => navigate('/area-associativa/associati')}>
                  <Users size={15} /> Associati
                </button>
              )}
              {hasSondaggiPerm && (
                <button className="btn dark" type="button"
                        style={{ background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.2)' }}
                        onClick={() => navigate('/area-associativa/sondaggi')}>
                  <ClipboardList size={15} /> Sondaggi
                </button>
              )}
            </div>
          </div>
        </section>

        {/* KPI — solo numeri reali */}
        <div className="kpis" style={{ marginTop: '20px' }}>
          <div className="kpi">
            <div className="n">{hasCalPerm ? (loadingEvents ? '…' : prossimi.length) : '—'}</div>
            <div className="l">Eventi in programma</div>
          </div>
          <div className="kpi">
            <div className="n">{hasCalPerm ? (loadingEvents ? '…' : eventiMese) : '—'}</div>
            <div className="l">Eventi · {meseLabel}</div>
          </div>
          <div className="kpi">
            <div className="n">{activeCariche.length}</div>
            <div className="l">Cariche attive</div>
          </div>
          {profile.associatesAccess ? (
            <div className="kpi">
              <div className="n">{totAssociati !== null ? totAssociati.toLocaleString('it-IT') : '…'}</div>
              <div className="l">Associati nel perimetro</div>
            </div>
          ) : (
            <div className="kpi">
              <div className="n">{hasCalPerm ? (loadingEvents ? '…' : conclusiTotali) : '—'}</div>
              <div className="l">Eventi conclusi</div>
            </div>
          )}
        </div>

        {/* Griglia: agenda a sinistra, Da fare + Profilo a destra */}
        <div className="dash-grid">
          {/* Colonna sinistra: calendario */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {hasCalPerm ? (
              <>
                <section className="card">
                  <div className="card-head">
                    <Calendar size={16} />
                    <h2>In programma</h2>
                    <div className="aux">
                      <button className="btn ghost sm" type="button" onClick={() => navigate('/area-associativa/calendario')}>
                        Calendario <ChevronRight size={13} className="arrow" />
                      </button>
                    </div>
                  </div>
                  {loadingEvents ? (
                    <div className="card-body muted" style={{ fontSize: '12.5px' }}>Caricamento…</div>
                  ) : prossimi.length > 0 ? (
                    prossimi.map((ev) => <EventRow key={ev._id} ev={ev} />)
                  ) : (
                    <div className="card-body muted" style={{ fontSize: '12.5px' }}>Nessun evento in programma.</div>
                  )}
                </section>

                {recenti.length > 0 && (
                  <section className="card">
                    <div className="card-head">
                      <CheckCircle2 size={16} />
                      <h2>Conclusi di recente</h2>
                    </div>
                    {recenti.map((ev) => <EventRow key={ev._id} ev={ev} />)}
                  </section>
                )}
              </>
            ) : (
              caricheCard
            )}
          </div>

          {/* Colonna destra: Da fare + Profilo (+ Cariche) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Da fare — bozze da completare, click → evento */}
            <section className="card">
              <div className="card-head">
                <Sparkles size={16} />
                <h2>Da fare</h2>
                {daFareCount > 0 && (
                  <div className="aux">
                    <span className="badge t-provinciale">{daFareCount}</span>
                  </div>
                )}
              </div>
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '16px' }}>
                {(loadingEvents || loadingTodo) && daFareCount === 0 ? (
                  <div className="muted" style={{ fontSize: '12.5px', textAlign: 'center', padding: '8px 0' }}>Caricamento…</div>
                ) : daFareCount > 0 ? (
                  <>
                    {bozze.map((ev) => (
                      <div key={ev._id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-md)', background: 'var(--un-ink-50)' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ font: '600 12.5px var(--font-body)', color: 'var(--text-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            Completa «{ev.nome}»
                          </div>
                          <div className="muted" style={{ fontSize: '11.5px', marginTop: '2px' }}>
                            Bozza · {new Date(ev.dataEvento).toLocaleDateString('it-IT', { day: 'numeric', month: 'long' })}
                          </div>
                        </div>
                        <button className="btn soft sm" type="button" onClick={() => navigate(`/area-associativa/calendario/${ev._id}`)}>
                          Riprendi
                        </button>
                      </div>
                    ))}
                    {verbaliDaLeggere.map((ev) => (
                      <div key={ev._id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-md)', background: 'var(--un-ink-50)' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ font: '600 12.5px var(--font-body)', color: 'var(--text-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            Leggi il verbale di «{ev.nome}»
                          </div>
                          <div className="muted" style={{ fontSize: '11.5px', marginTop: '2px' }}>
                            Verbale da leggere · {new Date(ev.dataEvento).toLocaleDateString('it-IT', { day: 'numeric', month: 'long' })}
                          </div>
                        </div>
                        <button className="btn soft sm" type="button" onClick={() => navigate(`/area-associativa/calendario/${ev._id}`)}>
                          Apri
                        </button>
                      </div>
                    ))}
                    {ticketTodo.map((tk) => (
                      <div key={tk._id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-md)', background: 'var(--un-ink-50)' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ font: '600 12.5px var(--font-body)', color: 'var(--text-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {tk.isMine ? (tk.daRispondere ? 'Rispondi a' : 'Segui') : 'Prendi in carico'} «{tk.subject}»
                          </div>
                          <div className="muted" style={{ fontSize: '11.5px', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            <span>{tk.canale || 'Supporto'} · {SUPPORT_STATUS_LABEL[tk.status] || tk.status} · {SUPPORT_PRIORITY_LABEL[tk.priority] || tk.priority}</span>
                            {tk.isMine && tk.daRispondere && (
                              <span style={{ font: '600 10px var(--font-body)', textTransform: 'uppercase', letterSpacing: '.03em', color: '#92400e', background: '#fde68a', borderRadius: '999px', padding: '1px 7px' }}>Da rispondere</span>
                            )}
                            {tk.notaDaLeggere && (
                              <span style={{ font: '600 10px var(--font-body)', textTransform: 'uppercase', letterSpacing: '.03em', color: '#92400e', background: '#fde68a', borderRadius: '999px', padding: '1px 7px' }}>Nota da leggere</span>
                            )}
                          </div>
                        </div>
                        <button className="btn soft sm" type="button" onClick={() => navigate(`/area-associativa/supporto/${tk._id}`)}>
                          Apri
                        </button>
                      </div>
                    ))}
                    {todo && todo.counts.totale > 0 && (
                      <button className="btn soft sm" type="button" style={{ alignSelf: 'flex-start' }} onClick={() => navigate('/area-associativa/supporto')}>
                        Vedi tutti i ticket{todo.counts.totale > ticketTodo.length ? ` (${todo.counts.totale})` : ''}
                      </button>
                    )}
                  </>
                ) : (
                  <div className="muted" style={{ fontSize: '12.5px', textAlign: 'center', padding: '8px 0', lineHeight: 1.6 }}>
                    Nessuna attività in sospeso.<br />
                    Qui troverai bozze da completare e verbali da leggere.
                  </div>
                )}
              </div>
            </section>

            {/* Profilo */}
            <section className="card">
              <div className="card-head">
                <User size={16} />
                <h2>Profilo</h2>
                {!editing && (
                  <div className="aux">
                    <button className="top-link" type="button" style={{ padding: '4px 10px' }} onClick={startEdit}>
                      <Pencil size={13} /> Modifica
                    </button>
                  </div>
                )}
              </div>
              <div className="card-body" style={{ paddingTop: '18px' }}>
                <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
                  <div className="relative group flex-shrink-0">
                    <div className="ava" style={{ width: '46px', height: '46px', borderRadius: '50%', background: 'var(--un-green-800)', color: '#fff', display: 'grid', placeItems: 'center', overflow: 'hidden', font: '600 15px var(--font-display)' }}>
                      {profile.foto ? (
                        <img src={`${UPLOADS_BASE}${profile.foto}`} alt="" style={{ width: '46px', height: '46px', objectFit: 'cover' }} />
                      ) : (
                        initials
                      )}
                    </div>
                    <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center gap-1 transition-opacity">
                      <label className="p-1 cursor-pointer text-white hover:text-emerald-300 transition-colors" title="Carica foto">
                        <Camera className="w-3.5 h-3.5" />
                        <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleFotoUpload} />
                      </label>
                      {profile.foto && (
                        <button onClick={handleFotoRemove} className="p-1 text-white hover:text-red-300 transition-colors" title="Rimuovi foto">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ font: '600 14px var(--font-display)', color: 'var(--text-strong)' }}>{profile.nome} {profile.cognome}</div>
                    {profile.emailPersonale && (
                      <div className="muted" style={{ fontSize: '12px', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <Mail size={12} /> {profile.emailPersonale}
                      </div>
                    )}
                    {profile.cellulare && (
                      <div className="muted" style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <Phone size={12} /> {profile.cellulare}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '6px', alignItems: 'center' }}>
                      <span className={`badge ${profile.tipo === 'interno' ? 's-inviato' : 't-provinciale'}`}>{profile.tipo}</span>
                      {profile.pin && (
                        <span className="muted" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px' }}>
                          <Lock size={11} /> PIN attivo
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Impresa collegata */}
                {profile.tipo === 'interno' && profile.associateId && (
                  <div style={{ marginTop: '16px', padding: '14px 16px', background: 'var(--un-green-50)', border: '1px solid var(--un-green-100)', borderRadius: 'var(--r-md)' }}>
                    <div className="mono-label" style={{ color: 'var(--un-green-700)', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <Building2 size={12} /> Impresa collegata
                    </div>
                    <div style={{ font: '600 13px var(--font-body)', marginTop: '5px', color: 'var(--text-strong)' }}>{profile.associateId.nominativo}</div>
                    <div className="muted" style={{ fontSize: '12px', marginTop: '2px' }}>
                      P.IVA {profile.associateId.partitaIVA} — {profile.associateId.citta} ({profile.associateId.provincia}) — {profile.associateId.categoria}
                    </div>
                  </div>
                )}

              </div>
            </section>

            {/* Cariche Attive (se il calendario occupa la colonna sinistra) */}
            {hasCalPerm && caricheCard}
          </div>
        </div>

        {/* Modale modifica profilo — stesso linguaggio della shell */}
        {editing && (
          <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setEditing(false); }}>
            <div className="modal" role="dialog" aria-modal="true" aria-label="Modifica profilo">
              <div className="modal-head">
                <Pencil size={15} />
                <h3>Modifica profilo</h3>
                <button className="modal-x" type="button" onClick={() => setEditing(false)} aria-label="Chiudi">
                  <X size={16} />
                </button>
              </div>

              <div className="modal-body">
                {/* Foto profilo: stessi handler dell'avatar in card */}
                <div className="mono-label" style={{ marginBottom: '10px' }}>Foto profilo</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                  <div className="ava" style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'var(--un-green-800)', color: '#fff', display: 'grid', placeItems: 'center', overflow: 'hidden', font: '600 17px var(--font-display)', flex: 'none' }}>
                    {profile.foto ? (
                      <img src={`${UPLOADS_BASE}${profile.foto}`} alt="" style={{ width: '56px', height: '56px', objectFit: 'cover' }} />
                    ) : (
                      initials
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <label className="top-link" style={{ cursor: 'pointer', padding: '5px 10px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <Camera size={13} /> {profile.foto ? 'Cambia foto' : 'Carica foto'}
                      <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleFotoUpload} aria-label="Scegli la foto profilo (JPEG, PNG o WebP, massimo 2 MB)" />
                    </label>
                    {profile.foto && (
                      <button type="button" className="top-link" style={{ padding: '5px 10px', display: 'inline-flex', alignItems: 'center', gap: '6px', color: 'var(--un-red-600, #b91c1c)' }} onClick={handleFotoRemove}>
                        <Trash2 size={13} /> Rimuovi
                      </button>
                    )}
                    <span className="muted" style={{ fontSize: '11.5px', flexBasis: '100%' }}>JPEG, PNG o WebP, massimo 2 MB.</span>
                  </div>
                </div>

                {/* Contatto */}
                <div className="mono-label" style={{ margin: '18px 0 10px' }}>Contatto</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                  <div>
                    <label className="field-label">Cellulare</label>
                    <input type="text" className="field" value={editCellulare} onChange={e => setEditCellulare(e.target.value)} />
                  </div>
                  <div>
                    <label className="field-label">Email personale (accesso diretto)</label>
                    <input type="email" className="field" value={editEmail} onChange={e => setEditEmail(e.target.value)}
                      placeholder="mail.personale@email.it" />
                  </div>
                </div>
                <div className="note green" style={{ marginTop: '12px' }}>
                  <b>A cosa serve l'email personale?</b><br />
                  Normalmente accedi alla piattaforma con la Partita IVA della tua impresa, e da li entri nell'area associativa tramite la card dedicata.
                  Impostando un'email personale potrai accedere <b>direttamente</b> a questa area associativa inserendo l'email nella pagina di login, senza passare dall'area impresa.
                  Deve essere un'email diversa da quella dell'impresa e di uso esclusivamente personale.
                </div>

                {/* Residenza */}
                <div className="mono-label" style={{ margin: '18px 0 10px' }}>Indirizzo di residenza</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                  <div>
                    <label className="field-label">Indirizzo</label>
                    <input type="text" className="field" value={editResidenza.indirizzo} onChange={e => setEditResidenza({ ...editResidenza, indirizzo: e.target.value })} />
                  </div>
                  <div>
                    <label className="field-label">N. Civico</label>
                    <input type="text" className="field" value={editResidenza.civico} onChange={e => setEditResidenza({ ...editResidenza, civico: e.target.value })} />
                  </div>
                  <div>
                    <label className="field-label">Citta</label>
                    <input type="text" className="field" value={editResidenza.citta} onChange={e => setEditResidenza({ ...editResidenza, citta: e.target.value })} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div>
                      <label className="field-label">Prov.</label>
                      <input type="text" className="field" value={editResidenza.provincia} maxLength={2}
                        style={{ textTransform: 'uppercase' }}
                        onChange={e => setEditResidenza({ ...editResidenza, provincia: e.target.value.toUpperCase() })} />
                    </div>
                    <div>
                      <label className="field-label">CAP</label>
                      <input type="text" className="field" value={editResidenza.cap} maxLength={5}
                        onChange={e => setEditResidenza({ ...editResidenza, cap: e.target.value })} />
                    </div>
                  </div>
                </div>

                {/* PIN */}
                <div className="mono-label" style={{ margin: '18px 0 10px' }}>Protezione PIN</div>
                <div className="note amber">
                  <b><Key size={12} style={{ display: 'inline', verticalAlign: '-1px', marginRight: '4px' }} />A cosa serve il PIN?</b><br />
                  Quando accedi con la Partita IVA, tutti coloro che la conoscono (es. i tuoi dipendenti) possono entrare nell'area riservata dell'impresa.
                  Senza PIN, questi stessi utenti potrebbero accedere anche alla tua area associativa cliccando sulla card dedicata.
                  <br /><b>Esempio:</b> un tuo dipendente accede con la P.IVA dello studio per gestire le pratiche.
                  Nella dashboard vede la card "Area Associativa". Se hai impostato un PIN, cliccandoci gli verra chiesto un codice che solo tu conosci, e non potra entrare.
                  Se non hai impostato il PIN, potra accedere liberamente alle informazioni del tuo ruolo associativo.
                </div>

                <div style={{ marginTop: '12px' }}>
                  {profile.pin ? (
                    <>
                      <div className="muted" style={{ fontSize: '12px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '5px', color: 'var(--un-green-700)' }}>
                        <Lock size={12} /> PIN attivo — l'area associativa e protetta
                      </div>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button type="button" className={`btn sm ${pinAction === 'set' ? 'soft' : 'ghost'}`}
                          onClick={() => setPinAction(pinAction === 'set' ? 'none' : 'set')}>
                          Cambia PIN
                        </button>
                        <button type="button" className={`btn sm ${pinAction === 'remove' ? 'danger' : 'ghost'}`}
                          onClick={() => setPinAction(pinAction === 'remove' ? 'none' : 'remove')}>
                          Rimuovi PIN
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="muted" style={{ fontSize: '12px', marginBottom: '8px' }}>
                        Nessun PIN impostato — l'area associativa e accessibile a chiunque acceda con la P.IVA
                      </div>
                      <button type="button" className={`btn sm ${pinAction === 'set' ? 'soft' : 'ghost'}`}
                        onClick={() => setPinAction(pinAction === 'set' ? 'none' : 'set')}>
                        Imposta PIN
                      </button>
                    </>
                  )}

                  {pinAction === 'set' && (
                    <input type="password" className="field" value={editPin} onChange={e => setEditPin(e.target.value)}
                      placeholder="Inserisci il nuovo PIN" maxLength={10}
                      style={{ marginTop: '10px', maxWidth: '220px' }} />
                  )}
                  {pinAction === 'remove' && (
                    <div style={{ marginTop: '10px', font: '400 12px var(--font-body)', color: 'var(--un-danger)' }}>
                      Il PIN verra rimosso al salvataggio. L'area associativa sara accessibile senza codice.
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-foot">
                <button type="button" className="btn ghost sm" onClick={() => setEditing(false)}>
                  <X size={14} /> Annulla
                </button>
                <button type="button" className="btn primary sm" onClick={handleSave} disabled={saving}>
                  <Save size={14} /> {saving ? 'Salvataggio…' : 'Salva'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AssociativeAppShell>
  );
};

export default AssociativeAreaDashboard;
